import { cn } from '@/lib/utils';
import React, {
  useState,
  useRef,
  useCallback,
  ChangeEvent,
  DragEvent,
} from 'react';

interface ExtendInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'onDrop'
  > {
  onHandleSelect?: (e: ChangeEvent<HTMLInputElement>) => void;
  onHandleDrop?: (e: DragEvent<HTMLDivElement>) => void;
  dropEleClassName?: string;
  children?: React.ReactNode;
}

const ExtendInput: React.FC<ExtendInputProps> = ({
  onHandleSelect,
  onHandleDrop,
  className = '',
  children,
  dropEleClassName,
  ...inputProps
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理拖拽进入区域
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  // 处理拖拽离开区域
  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  // 处理文件放置
  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const { files } = e.dataTransfer;
      if (files && files.length > 0) {
        if (onHandleDrop) {
          onHandleDrop(e);
        }
      }
    },
    [onHandleDrop, onHandleSelect],
  );

  // 处理文件选择
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0 && onHandleSelect) {
      onHandleSelect(e);
    }
  };

  // 点击触发文件选择
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // 允许重复选择相同文件
      fileInputRef.current.click();
    }
  };

  return (
    <div className={cn('cursor-pointer', className)}>
      <div
        className={cn('w-full h-full', dropEleClassName)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop ? handleDrop : undefined}
        onClick={handleClick}
      >
        {children}
      </div>

      {/* 隐藏的原生文件输入框 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        {...inputProps}
      />
    </div>
  );
};

export default ExtendInput;
